import { RefObject } from 'react';
import { FC } from 'react';
import type { PillItem } from 'components/home/content/product-carousel.content';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import Button, { ButtonProps } from '../Button';
import Pill from 'components/global/Pill';
import { TypeBodyLarge, TypePageSubhead, TypeBody } from '../Typography';
import {
  CardFrame,
  CardPills,
  CardBlock,
  CardLabel,
  CardCaption,
  MediaContainer,
} from './card.styled';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import { useTransparentVideoSrc } from 'hooks/use-transparent-video-src';
import Link from 'next/link';
import Image from 'next/image';

export interface CardProps {
  title: string;
  microNavTitle?: string;
  label?: string;
  pills?: PillItem[];
  media: {
    src?: string;
    alt: string;
    width: number;
    height: number;
    playbackId?: string;
    devPlaybackId?: string;
    videoScale?: number;
    videoSrc?: string;
    webmVideoSrc?: string;
  };
  primaryButton: ButtonProps;
  note?: string;
  desktopNote?: string;
  ref?: any;
  className?: string;
  videoRefs?: RefObject<any[]>;
  /** False shows only the poster and fetches no video. */
  loadVideo?: boolean;
  index: number;
  onClick?: (e: any) => void;
  onMouseEnter?: (e: any) => void;
  active: boolean;
  color: 'seedgreen' | 'snowwhite';
  labelColor?:
    | 'seedgreen'
    | 'snowwhite'
    | 'amprimary'
    | 'pmprimary'
    | 'dmprimary';
  buttonProps?: Omit<ButtonProps, 'label' | 'color' | 'size'>;
}

const Card: FC<CardProps> = ({
  title,
  label,
  labelColor = 'seedgreen',
  pills,
  media,
  primaryButton,
  note,
  desktopNote,
  ref,
  onClick,
  onMouseEnter,
  className,
  videoRefs,
  loadVideo = true,
  index,
  active,
  color,
  buttonProps = {},
}) => {
  const isSmall = useClientSideMediaQuery(960);
  const isSmallMobile = useClientSideMediaQuery(450);

  const textColor = color === 'seedgreen' ? 'inverse-primary' : 'primary';
  const buttonHoverColor =
    color === 'seedgreen' ? ('snowwhite' as const) : ('seedgreen' as const);

  const videoSrc = useTransparentVideoSrc(media, !!loadVideo);
  const setVideoRef = (el: HTMLVideoElement | null) => {
    if (videoRefs?.current) {
      videoRefs.current[index] = el;
    }
  };

  return (
    <CardFrame
      as={primaryButton.url ? Link : 'div'}
      href={primaryButton.url}
      className={`card ${className || ''}`}
      ref={ref}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      $color={color}
      $active={active}
    >
      {label && (
        <CardLabel size='small' color={labelColor}>
          {label}
        </CardLabel>
      )}
      <CardBlock
        className='block-top'
        hoverColor={textColor}
        stackSize='xSmall'
      >
        <CardPills>
          {pills ? (
            pills.map((pill, index) => (
              <Pill
                key={index}
                light={textColor === 'inverse-primary'}
                trademark={pill.trademark}
                registered={pill.registered}
                size={isSmall ? 'xs' : 'sm'}
                lineHeight={isSmall ? '7px' : '8px'}
                fontWeight={350}
              >
                {pill.text}
              </Pill>
            ))
          ) : (
            // way less annoying re: layoutshift to just hide the pill and keep a div
            // that maintains the same height rules
            <Pill
              visibility='hidden'
              key={index}
              light={textColor === 'inverse-primary'}
              size={isSmall ? 'xs' : 'sm'}
              lineHeight={isSmall ? '7px' : '8px'}
              fontWeight={350}
            >
              {''}
            </Pill>
          )}
        </CardPills>
        {!isSmall ? (
          <TypePageSubhead color={textColor}>{title}</TypePageSubhead>
        ) : null}
        {isSmall && !isSmallMobile ? (
          <TypeBodyLarge color={textColor}>{title}</TypeBodyLarge>
        ) : isSmallMobile ? (
          <TypeBody color={textColor}>{title}</TypeBody>
        ) : null}
      </CardBlock>
      <MediaContainer $posterUrl={media.src}>
        {media.videoSrc ? (
          <video
            src={videoSrc}
            // allows us to display the video faster in safari mobile, which is strict about autoplay policies
            poster={media.src}
            width={'100%'}
            muted={true}
            playsInline
            loop
            onClick={onClick}
            ref={setVideoRef}
          />
        ) : media.src ? (
          <Image
            alt={media.alt}
            width={media.width}
            height={media.height}
            src={media.src || ''}
            style={{ width: media.width, height: '100%', objectFit: 'contain' }}
          />
        ) : null}
      </MediaContainer>

      <CardBlock hoverColor={textColor} className='block-bottom'>
        <Button
          label={primaryButton.label}
          showArrow={active}
          size={isSmall ? 'medium' : 'large'}
          hoverColor={buttonHoverColor}
          onClick={() =>
            buttonEventHandler(title + primaryButton.label, 'Card')
          }
          {...buttonProps}
        />
        {note && (
          <CardCaption color={textColor}>
            {!isSmall && desktopNote ? desktopNote : note}
          </CardCaption>
        )}
      </CardBlock>
    </CardFrame>
  );
};

export default Card;
